import React from 'react'
import { Grid } from '@mui/material'
import SectionTitle from '../../../../components/form/SectionTitle'
import { AdGroupInput, AdGroupCreateInput } from '../../../../__generated__/graphql'
import Cost from './Cost'
import DailyBudget from './DailyBudget'
import BudgetCapping from './BudgetCapping'
import Dates from './Dates'
import SpentBudget from './SpentBudget'
import BudgetPlans from './budgetPlans/BudgetPlans'
import { useCanEditAdGroup } from '../../../../utils/hooks/adGroup/useCanEditAdGroup'
import { Section } from '../../../../components/layout/Section'
import { AdgroupBudgetUserPermissions } from '../../../company/userPermissions'

export interface BudgetAndDatesProps {
  adgroup: AdGroupInput | AdGroupCreateInput
  /** Forwarded to budget sub-fields; when true, spent-budget readout is hidden (see SpentBudget). */
  isWizard?: boolean
  /** When set, overrides `useCanEditAdGroup()`. */
  canEdit?: boolean
  /** @default true */
  showSectionTitle?: boolean
  /** When true, used in the create wizard: cost and daily budget share a row; other groups span the full width. */
  fullWidth?: boolean
  /** Advertiser company budget rights. When set, replaces the signed-in user's budget permissions. */
  budgetPermissions?: AdgroupBudgetUserPermissions
  /** When true, shows the planned budget changes control (settings page only). */
  showBudgetPlans?: boolean
  /** When true, type selects include an empty “unchanged” option (budget plans). */
  allowUnchanged?: boolean
}

export interface FieldsProps {
  adgroup: AdGroupInput | AdGroupCreateInput
  isWizard?: boolean
  canEdit?: boolean
  fullWidth?: boolean
  /** When true, type selects include an empty “unchanged” option (budget plans). */
  allowUnchanged?: boolean
}

export const BudgetAndDates: React.FC<BudgetAndDatesProps> = ({
  adgroup,
  isWizard,
  canEdit: canEditProp,
  showSectionTitle = true,
  fullWidth = false,
  budgetPermissions,
  showBudgetPlans = false,
  allowUnchanged = false,
}): JSX.Element => {
  const hookCanEdit = useCanEditAdGroup()
  const canEdit = canEditProp !== undefined ? canEditProp : hookCanEdit
  const adGroupId = 'id' in adgroup ? String(adgroup.id) : undefined

  const fields = (
    <>
      <Grid container spacing={2}>
        <Cost
          adgroup={adgroup}
          isWizard={isWizard}
          canEdit={canEdit}
          fullWidth={fullWidth}
          allowUnchanged={allowUnchanged}
        />
        <DailyBudget
          adgroup={adgroup}
          isWizard={isWizard}
          canEdit={canEdit}
          fullWidth={fullWidth}
          allowUnchanged={allowUnchanged}
        />
        <BudgetCapping
          adgroup={adgroup}
          isWizard={isWizard}
          canEdit={canEdit}
          fullWidth={fullWidth}
          allowUnchanged={allowUnchanged}
          budgetPermissions={budgetPermissions}
        />
      </Grid>
      <Grid container spacing={2} sx={{ marginTop: 2 }}>
        <Dates
          adgroup={adgroup}
          isWizard={isWizard}
          canEdit={canEdit}
          fullWidth={fullWidth}
          allowUnchanged={allowUnchanged}
        />
        {!allowUnchanged && <SpentBudget isWizard={isWizard} />}
      </Grid>
    </>
  )

  const budgetPlans =
    showBudgetPlans && !allowUnchanged && canEdit && adGroupId != null ? (
      <BudgetPlans adGroupId={adGroupId} adgroup={adgroup} />
    ) : null

  if (!showSectionTitle) return fields

  return (
    <>
      <Section>
        <SectionTitle title="adgroup.budgetAndDates.title" />
        {fields}
      </Section>
      {budgetPlans}
    </>
  )
}
